import { createContext, useMemo } from "react";
import { createTheme } from "@mui/material/styles";
import { gridClasses } from "@mui/x-data-grid";

/** Matrize brand — navy, bright blue, cyan (from brand guidelines). */
const brand = {
  navy: "#002B5C",
  navyDeep: "#001a3a",
  navyMid: "#0a3d6b",
  navySoft: "#0f4d7a",
  bright: "#007BFF",
  brightHover: "#0066d4",
  brightPressed: "#0052b3",
  cyan: "#00D2FF",
  cyanSoft: "#87CEEB",
  white: "#FFFFFF",
  offWhite: "#f5f8fc",
};

// color design tokens export
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#f0f6fc",
          200: "#dce8f4",
          300: "#b8cce3",
          400: "#8fa8c4",
          500: "#6b879f",
          600: "#4d6a82",
          700: "#3a5268",
          800: "#2a3d4f",
          900: "#1a2835",
        },
        primary: {
          100: "#e8f4ff",
          200: "#b8daf8",
          300: "#6ba3db",
          400: brand.navySoft,
          500: brand.navy,
          600: brand.navyDeep,
          700: "#001428",
          800: "#000f1f",
          900: "#000814",
        },
        /** Legacy name `greenAccent` — palette is navy / blue (no green). */
        greenAccent: {
          100: "#e8f4ff",
          200: "#c5daf5",
          300: "#8eb8e8",
          400: "#3d7abf",
          500: "#007bff",
          600: "#0056b3",
          700: "#003d82",
          800: "#002b5c",
          900: "#001428",
        },
        redAccent: {
          100: "#fde8e8",
          200: "#f9c4c4",
          300: "#f19999",
          400: "#e86e6e",
          500: "#d94848",
          600: "#b33939",
          700: "#862b2b",
          800: "#5a1d1d",
          900: "#2d0f0f",
        },
        blueAccent: {
          100: "#e6f4ff",
          200: "#b3d9ff",
          300: "#66b8ff",
          400: brand.cyanSoft,
          500: brand.cyan,
          600: brand.bright,
          700: brand.brightHover,
          800: "#004a99",
          900: brand.navyDeep,
        },
      }
    : {
        grey: {
          100: "#0d2137",
          200: "#1a3350",
          300: "#264563",
          400: "#3a5a78",
          500: "#4d6d8a",
          600: "#6b879f",
          700: "#8fa8c4",
          800: "#b8cce3",
          900: "#e8f0f8",
        },
        primary: {
          100: brand.navy,
          200: "#0a3d6b",
          300: "#0f4d7a",
          400: brand.offWhite,
          500: brand.white,
          600: "#e8f0f8",
          700: "#dce8f4",
          800: "#cfdfe9",
          900: "#f5f8fc",
        },
        greenAccent: {
          100: "#000814",
          200: "#001428",
          300: "#002b5c",
          400: "#003d82",
          500: "#007bff",
          600: "#4d9eff",
          700: "#8bb8f0",
          800: "#c5daf5",
          900: "#e8f4ff",
        },
        redAccent: {
          100: "#2d0f0f",
          200: "#5a1d1d",
          300: "#862b2b",
          400: "#b33939",
          500: "#d94848",
          600: "#e86e6e",
          700: "#f19999",
          800: "#f9c4c4",
          900: "#fde8e8",
        },
        blueAccent: {
          100: brand.navyDeep,
          200: "#004a99",
          300: brand.brightHover,
          400: brand.bright,
          500: brand.cyan,
          600: brand.cyanSoft,
          700: "#66b8ff",
          800: "#b3d9ff",
          900: "#e6f4ff",
        },
      }),
});

const fontStack = ["Inter", "system-ui", "Segoe UI", "Roboto", "sans-serif"].join(
  ","
);

// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            primary: {
              main: brand.bright,
              contrastText: "#ffffff",
            },
            secondary: {
              main: colors.greenAccent[400],
              contrastText: "#ffffff",
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.primary[500],
              paper: colors.primary[400],
            },
            text: {
              primary: colors.grey[100],
              secondary: colors.grey[300],
            },
          }
        : {
            primary: {
              main: brand.bright,
              contrastText: "#ffffff",
            },
            secondary: {
              main: colors.greenAccent[500],
              contrastText: "#ffffff",
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: brand.white,
              paper: brand.offWhite,
            },
            text: {
              primary: brand.navy,
              secondary: colors.grey[500],
            },
          }),
    },
    typography: {
      fontFamily: fontStack,
      /** Slightly larger base so body text (and rem-based UI) reads more clearly. */
      fontSize: 14,
      h1: { fontFamily: fontStack, fontSize: 40, fontWeight: 700 },
      h2: { fontFamily: fontStack, fontSize: 32, fontWeight: 700 },
      h3: { fontFamily: fontStack, fontSize: 24, fontWeight: 600 },
      h4: { fontFamily: fontStack, fontSize: 20, fontWeight: 600 },
      h5: { fontFamily: fontStack, fontSize: 16, fontWeight: 600 },
      h6: { fontFamily: fontStack, fontSize: 14, fontWeight: 600 },
    },
    components: {
      /** All @mui/x-data-grid tables — larger type and row height for readability. */
      MuiDataGrid: {
        defaultProps: {
          rowHeight: 58,
          headerHeight: 56,
        },
        styleOverrides: {
          root: {
            fontSize: "16px",
            [`& .${gridClasses["cell--textLeft"]}`]: {
              justifyContent: "center",
            },
            [`& .${gridClasses["cell--textRight"]}`]: {
              justifyContent: "center",
            },
            [`& .${gridClasses.cellContent}`]: {
              textAlign: "center",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              overflow: "hidden",
            },
            [`& .${gridClasses.columnHeader}`]: {
              justifyContent: "center",
            },
            [`& .${gridClasses.columnHeaderDraggableContainer}`]: {
              justifyContent: "center",
              alignItems: "center",
            },
            [`& .${gridClasses.columnHeaderTitleContainer}`]: {
              justifyContent: "center",
            },
            [`& .${gridClasses.columnHeaderTitleContainerContent}`]: {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            },
            [`& .${gridClasses["columnHeader--alignRight"]} .${gridClasses.columnHeaderDraggableContainer}`]:
              {
                flexDirection: "row",
                justifyContent: "center",
              },
            [`& .${gridClasses["columnHeader--alignRight"]} .${gridClasses.columnHeaderTitleContainer}`]:
              {
                flexDirection: "row",
                justifyContent: "center",
              },
            [`& .${gridClasses.cellCheckbox}`]: {
              justifyContent: "center",
            },
            [`& .${gridClasses.footerCell}`]: {
              justifyContent: "center",
              textAlign: "center",
            },
          },
          cell: {
            fontSize: "16px",
            lineHeight: 1.45,
            justifyContent: "center",
          },
          columnHeader: {
            fontSize: "15px",
            justifyContent: "center",
          },
          columnHeaderTitle: {
            fontSize: "15px",
            fontWeight: 600,
            width: "100%",
            maxWidth: "100%",
            textAlign: "center",
            margin: "0 auto",
          },
          footerContainer: {
            fontSize: "15px",
            justifyContent: "center",
            "& .MuiTablePagination-toolbar": {
              justifyContent: "center",
            },
          },
          toolbarContainer: {
            fontSize: "15px",
            "& .MuiButton-text": { fontSize: "15px" },
          },
          menu: {
            fontSize: "15px",
          },
          panelContent: {
            fontSize: "15px",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 10, textTransform: "none", fontWeight: 600 },
          containedPrimary: {
            backgroundColor: brand.bright,
            "&:hover": { backgroundColor: brand.brightHover },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { borderRadius: 12 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 10 },
        },
      },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = () => {
  const theme = useMemo(
    () => createTheme(themeSettings("light")),
    []
  );

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {},
    }),
    []
  );

  return [theme, colorMode];
};
